import type { SetStateAction} from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePrevious } from "@chakra-ui/react";
import { Emitter } from "@irrelon/emitter";
import { useCreateUrl } from "@/services/useCreateUrl";
import { useHybridRouter } from "@/services/router/useHybridRouter";
import type { Json } from "@/types/Json";
import type { HybridNavigateOptions } from "@/types/HybridNavigateOptions";

export interface CanonicalStateSupportedEvents {
	GetStateValue: () => string | undefined;
}

export const canonicalStateEmitter = new Emitter<CanonicalStateSupportedEvents>();

export interface CanonicalStateTransformer<StateType> {
	encode: (value: StateType | undefined, fallbackValue: StateType) => string | undefined;
	decode: (value: string | undefined, fallbackValue: StateType) => StateType;
}

export interface CanonicalStateOptions extends HybridNavigateOptions {
	autoCommit: boolean;
}

export const booleanTransformer: CanonicalStateTransformer<boolean> = {
	encode: (value: boolean | undefined, fallbackValue: boolean) => {
		const finalValue = value !== undefined ? value : fallbackValue;
		return finalValue ? "true" : undefined;
	},
	decode: (value: string | undefined, fallbackValue: boolean) => {
		const finalValue = value !== undefined ? value : fallbackValue;
		return finalValue === "true";
	}
};

export const stringTransformer: CanonicalStateTransformer<string> = {
	encode: (value: string | undefined, fallbackValue: string) => {return value !== undefined ? value : fallbackValue},
	decode: (value: string | undefined, fallbackValue: string) => {return value !== undefined ? value : fallbackValue}
};

export const jsonTransformer: CanonicalStateTransformer<any> = {
	encode: (value: Json | undefined, fallbackValue: Json): string => {return value !== undefined ? JSON.stringify(value) : JSON.stringify(fallbackValue)},
	decode: (value: string | undefined, fallbackValue: Json): Json => {return value !== undefined ? JSON.parse(value) : fallbackValue}
};

export function useCanonicalState<StateType = Json>(
	queryParameterName: string,
	defaultValue: StateType,
	transformer: CanonicalStateTransformer<StateType> = jsonTransformer,
	options: CanonicalStateOptions = { autoCommit: true, shallow: true }
): [
		StateType,
		(valOrFunc: SetStateAction<StateType>) => void,
		() => void,
		string | undefined,
		string,
		CanonicalStateTransformer<StateType>
	] {
	const { query, push } = useHybridRouter("useCanonicalState");
	const createUrl = useCreateUrl();
	const [internalStateValue, setInternalStateValue] = useState<StateType>(defaultValue);

	const urlParamValue = query[queryParameterName];
	const previousUrlParamValue = usePrevious(urlParamValue);
	const hasUrlParamChanged = urlParamValue !== previousUrlParamValue;

	const encodedState = useMemo(() => {return transformer.encode(internalStateValue, defaultValue)}, [defaultValue, internalStateValue, transformer]);

	useEffect(() => {
		const getStateValueListener = () => {return encodedState};

		canonicalStateEmitter.on("GetStateValue", queryParameterName, getStateValueListener);

		return () => {
			canonicalStateEmitter.off("GetStateValue", queryParameterName, getStateValueListener);
		};
	}, [encodedState, queryParameterName]);

	/**
	 * When the url parameter value changes, we use this as the state,
	 * overriding any previously set value and also taking precedence over the
	 * default value.
	 */
	useEffect(() => {
		if (!hasUrlParamChanged) {return;}
		const decodedValue = transformer.decode(urlParamValue as string, defaultValue);
		setInternalStateValue(decodedValue);
	}, [defaultValue, hasUrlParamChanged, transformer, urlParamValue]);

	/**
	 * Call this function to update the state. Takes either the value to update to
	 * or a function takes the current state value as an argument, and returns the
	 * new value to update to.
	 */
	const setStateValue = useCallback(
		(valOrFunc: SetStateAction<StateType>, commit = false) => {
			if (typeof valOrFunc !== "function") {
				setInternalStateValue(valOrFunc);
				if (options.autoCommit || commit) {
					push(createUrl("", { [queryParameterName]: transformer.encode(valOrFunc, defaultValue) }), options);
				}

				return;
			}

			setInternalStateValue((currentValue: StateType | undefined) => {
				// @ts-ignore
				const value = valOrFunc(currentValue);

				if (options.autoCommit || commit) {
					push(createUrl("", { [queryParameterName]: transformer.encode(value, defaultValue) }), options);
				}

				return value;
			});
		},
		[createUrl, defaultValue, options, push, queryParameterName, transformer]
	);

	const onCommit = useCallback(() => { }, []);

	return [internalStateValue, setStateValue, onCommit, encodedState, queryParameterName, transformer];
}
